import React, { useEffect, useState, useRef } from "react";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { toast } from "@/components/ui/UseToast";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet";
import { Button } from "@/components/ui/Button";
import { Separator } from "@/components/ui/Separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { LoadingButton } from "@/components/ui/loading-button/LoadingButton";
import { FloatingLabelInput } from "@/components/ui/floating-lable/Floating";
import { ConfirmationDialog } from "@/components/ui/common/ConfirmationDailog";
import { InputTags } from "@/components/ui/input-tags/Tags";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/Command";
import { Check, ChevronDownIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useGetDepartmentsQuery } from "@/services/DepartmentApi";
import { useGetAllCounterQuery, useGetCounterListQuery } from "@/services/CounterApi";
import { useGetStaffListQuery } from "@/services/StaffsApi";
import { useDebounce } from "@/hooks/UseDebounce";
import PhoneInputField from "@/components/custom-component/PhoneInputField";
import { useGetServiceQuery, useGetServiceTagsQuery } from "@/services/ServiceApis";
import { useLazyGetUserByUsernameQuery } from "@/services/UserApi";
import { Badge } from "@/components/ui/Badge";
import { useGenerateUserTicketFromserviceMutation } from "@/services/SelfServiceKiosk";
import { ErrorType } from "@/services/AuthService";
import { TicketActionOnStatus } from "@/services/LiveQueueService";
import { status } from "@/constants/GlobalFilter";

interface AddTicketFormProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  refetch: () => void;
  initialDepartment?: number;
  initialService?: number;
  initialTitle?: string;
  initialCounter?: number;
}

interface TicketFormValues {
  department_id: number | string;
  service_id: number | string;
  title: string;
  description?: string;
  id?:string;
  customer_its?: string;
  customer_name?: string;
  phone_number?: string;
  email?: string;
  counter_id: number | string;
  status: string;
  assigned_to?: number | string;
  appointment_date: string;
  appointment_time: string;
}

const initialValues: TicketFormValues = {
  department_id: "",
  service_id: "",
  title: "",
  description: "",
  id: "",
  customer_its: "",
  customer_name: "",
  phone_number: "",
  email: "",
  counter_id: "",
  status: Object.values(TicketActionOnStatus)[0], // Use first value from enum
  assigned_to: "",
  appointment_date: "",
  appointment_time: "",
};


const AddTicketForm: React.FC<AddTicketFormProps> = ({
  isOpen,
  setOpen,
  refetch,
  initialDepartment,
  initialService,
  initialTitle,
  initialCounter,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<
    string | undefined
  >(initialDepartment ? String(initialDepartment) : undefined);
  const [selectedService, setSelectedService] = useState<string | undefined>(
    initialService ? String(initialService) : undefined
  );
  const [itsSearchTerm, setItsSearchTerm] = useState("");
  const debouncedItsSearch = useDebounce(itsSearchTerm, 500);
  const [itsUserResults, setItsUserResults] = useState<any[]>([]);
  const isResettingService = useRef(false);
  const [titleInput, setTitleInput] = useState(initialTitle || "");
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [isTitlePopoverOpen, setIsTitlePopoverOpen] = useState(false);

  // Add this to fetch users by ITS
  const [getUserByUsername] = useLazyGetUserByUsernameQuery();

  // Form handling
  const form = useForm<TicketFormValues>({
    mode: "all",
    defaultValues: {
      ...initialValues,
      department_id: initialDepartment || "",
      service_id: initialService || "",
      title: initialTitle || "",
      counter_id: initialCounter || "",
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
    clearErrors,
  } = form;

  // Watch form values
  const watchedDepartment = watch("department_id");
  const watchedService = watch("service_id");
  const watchedIts = watch("customer_its");

  // Fetch data
  const { data: departmentsData } = useGetDepartmentsQuery({
    query: "",
  });
  const { data: servicesData, isLoading: isStaffDataLoading } =
    useGetServiceQuery(
      {
        query: selectedDepartment ? `department_id=${selectedDepartment}` : "",
      },
      {
        skip: !selectedDepartment,
      }
    );

  // Update the counter query to include service_id in the query string
  const { data: countersData, isLoading: isCounterDataLoading } =
    useGetAllCounterQuery(
      {
        query: selectedService ? `service_id=${selectedService}` : "",
      },
      {
        skip: !selectedService, // Only fetch counters when service is selected
      }
    );
  const { data: staffData } = useGetStaffListQuery({ query: "" });
  const { data: serviceTags, isLoading: isServiceTagsLoading } = useGetServiceTagsQuery(
    { serviceId: selectedService || "" },
    { skip: !selectedService }
  );

  // Set up department options
  const departmentOptions = React.useMemo(
    () =>
      departmentsData?.data?.map((dept) => ({
        label: dept.name,
        value: dept.id,
      })) || [],
    [departmentsData?.data]
  );

  // Set up service options based on selected department
  const serviceOptions = React.useMemo(
    () =>
      servicesData?.data?.map((service) => ({
        label: service.name,
        value: service.id,
      })) || [],
    [servicesData?.data]
  );

  // Set up counter options
  const counterOptions = React.useMemo(
    () =>
      countersData?.data?.map((counter) => ({
        label: `${counter.code}`,
        value: counter.id,
      })) || [],
    [countersData?.data]
  );

  // Set up staff options
  const staffOptions = React.useMemo(
    () =>
      staffData?.map((staff) => ({
        label: staff.label,
        value: staff.value,
        designation:staff.designation
      })) || [],
    [staffData]
  );

  // Effect to update services when department changes
  useEffect(() => {
    if (watchedDepartment) {
      setSelectedDepartment(String(watchedDepartment));

      // Only clear service if this isn't the initial render with initialService
      if (!initialService) {
        isResettingService.current = true;
        setValue("service_id", "");
        setSelectedService(undefined);
        setValue("counter_id", "");
        // Reset the flag after state updates
        setTimeout(() => {
          isResettingService.current = false;
        }, 0);
      }
    }
  }, [watchedDepartment, initialService, setValue]);

  // New effect to update counters when service changes
  useEffect(() => {
    // Skip this effect if we're in the middle of resetting
    if (isResettingService.current) return;

    if (watchedService) {
      setSelectedService(String(watchedService));
      // Clear counter selection when service changes
      if (!initialCounter) {
        setValue("counter_id", "");
      }
    } else {
      setSelectedService(undefined);
    }
  }, [watchedService, initialCounter, setValue]);

  // Effect to search for ITS users
  useEffect(() => {
    const searchItsUsers = async () => {
      if (debouncedItsSearch && debouncedItsSearch.length >= 2) {
        try {
          // Call the real API instead of using mock data
          const response = await getUserByUsername({ username: debouncedItsSearch }).unwrap();
          setItsUserResults(response || []);
        } catch (error) {
          console.error("Error searching ITS users", error);
          setItsUserResults([]);
        }
      } else {
        setItsUserResults([]);
      }
    };

    searchItsUsers();
  }, [debouncedItsSearch, getUserByUsername]);

  // Handle form submission
  const [generateTicket, { isLoading: isGeneratingTicket }] = useGenerateUserTicketFromserviceMutation();

  const onSubmit = async (data: TicketFormValues) => {
    try {
      // Time validation for today's appointments
      if (data.appointment_date === new Date().toISOString().split("T")[0]) {
        const now = new Date();
        const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
        const selectedTime = data.appointment_time;
        
        if (selectedTime < currentTime) {
          toast({
            variant: "destructive",
            title: "Invalid time",
            description: "Appointment time cannot be in the past"
          });
          return;
        }
      }
      
      // Format time as HH:MM:SS
      const formattedTime = data.appointment_time.includes(':') 
        ? `${data.appointment_time}${data.appointment_time.split(':').length < 3 ? ':00' : ''}`
        : `${data.appointment_time}:00`;

      let eta = 20;
      const today = new Date().toISOString().split('T')[0];
      if (data.appointment_date === today) {
        const now = new Date();
        const appointmentTime = new Date(
          `${data.appointment_date}T${formattedTime}`
        );
        
        // Calculate difference in minutes
        const diffInMinutes = Math.floor(
          (appointmentTime.getTime() - now.getTime()) / (1000 * 60)
        );
        
        // Only set ETA if difference is greater than 60 minutes
        eta = diffInMinutes > 60 ? diffInMinutes : 20;
      }
      // Format data for the API
      const ticketData = {
        service_id: Number(data.service_id),
        title: data.title,
        date: data.appointment_date,
        time: formattedTime, // Using formatted time with seconds
        is_admin: true,
        description: data.description,
        user_id: data.customer_its ? Number(data.id) : undefined,
        eta: eta, // Using calculated ETA
        counter: Number(data.counter_id),
        assigned_to: data.assigned_to ? String(data.assigned_to) : undefined,
        status: data.status,
      };

      // Call the mutation
      console.log("Submitting ticket data:", ticketData);
      const response = await generateTicket(ticketData).unwrap();
      
      toast({
        variant: "success", 
        title: "Success",
        description: `Ticket ${response.ticket_number} created successfully`,
      });

      handleClose();
      refetch();
      reset(initialValues); // Reset form after successful submission
      setTitleInput("")
      setItsSearchTerm("")
    } catch (error: unknown) {
      console.error("Error creating ticket", error);
      const errorData = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: errorData?.data?.message || "An error occurred while creating the ticket",
      });
    }
  };

  // Handle validation errors
  const onError = (errors: any) => {
    console.error("Form errors:", errors);
    toast({
      variant: "destructive",
      description: "Please fill all the required fields correctly",
    });
  };

  // Handle user selection from ITS search
  const handleSelectItsUser = (user: any) => {
    setValue("id",user.id)
    setValue("customer_its", user.username);
    setValue("customer_name", `${user.first_name} ${user.last_name}`);
    setValue("phone_number", user.phone_num || "");
    setValue("email", user.email || "");
    setItsUserResults([]);
    setItsSearchTerm(user.username);
    document.activeElement instanceof HTMLElement &&
      document.activeElement.blur();
  };

  // Dialog handling for closing form
  const openDialog = () => {
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  const handleConfirm = () => {
    closeDialog();
    handleClose();
  };

  const handleClose = () => {
    clearErrors();
    reset(initialValues);
    setTitleInput("");
    setItsSearchTerm("");
    setOpen(false);
  };

  // Handle tag selection
  const handleTagSelect = (tag: string) => {
    setSelectedTag(tag);
    setValue("title", tag);
    setTitleInput(tag);
    setIsTitlePopoverOpen(false); // Close the popover after selection
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow spaces between words but normalize multiple spaces to single space
    const value = e.target.value.replace(/\s+/g, " ");
    setTitleInput(value);
    setValue("title", value);

    // If input doesn't match any tags, clear selected tag
    if (serviceTags?.tags && !serviceTags.tags.includes(value.trim())) {
      setSelectedTag(null);
    }
  };

  useEffect(() => {
    if (isOpen) {
      reset({
        ...initialValues,
        department_id: initialDepartment || "",
        service_id: initialService || "",
        title: initialTitle || "",
        counter_id: initialCounter || "",
      });
      setSelectedDepartment(initialDepartment ? String(initialDepartment) : undefined);
      setSelectedService(initialService ? String(initialService) : undefined);
      setTitleInput(initialTitle || "");
    }
  }, [isOpen, initialDepartment, initialService, initialTitle, initialCounter, reset]);

  return (
    <Sheet open={isOpen}>
      <SheetContent
        hideCloseButton
        className="!max-w-[1050px] p-0 custom-scrollbar sm:w-[90%] sm:max-w-2xl"
      >
        <FormProvider {...form}>
          <form
            onSubmit={handleSubmit(onSubmit, onError)}
            className="flex flex-col gap-4 bg-gray-100 h-screen"
            noValidate
          >
            <SheetHeader className="sticky top-0 z-40 px-4 py-3 bg-white border-2 border-transparent border-b-gray-200">
              <SheetTitle>
                <div className="flex justify-between gap-5 items-center bg-white">
                  <p className="font-semibold">Create Ticket</p>

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={openDialog}
                      className="w-[100px] gap-2 bg-transparent border border-primary text-black hover:border-primary hover:bg-muted"
                    >
                      <i className="fa-regular fa-x text-xs font-semibold"></i>
                      Cancel
                    </Button>
                    <LoadingButton
                      type="submit"
                      loading={isSubmitting || isGeneratingTicket}
                      disabled={isSubmitting || isGeneratingTicket}
                    >
                      {!isGeneratingTicket && !isSubmitting && (
                        <i className="fa-regular fa-floppy-disk text-base px-1"></i>
                      )}
                      Save
                    </LoadingButton>
                  </div>
                </div>
              </SheetTitle>
            </SheetHeader>

            <SheetDescription className="p-4 mx-4 bg-white rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                {/* Department Dropdown */}
                <div className="relative">
                  <Controller
                    control={control}
                    name="department_id"
                    rules={{ required: "Required" }}
                    render={({ field: { onChange, value } }) => (
                      <>
                        <div className="relative w-full">
                          <label className="absolute text-xs font-medium left-2 -top-2 z-10 bg-white px-1 text-gray-600">
                            Department
                            <span className="text-red-500">*</span>
                          </label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button className="capitalize w-full scale-100 shadow-sm border-checkboxborder border-[1px] bg-white text-gray-600 hover:bg-white">
                                <span className="w-full text-left font-normal">
                                  {value
                                    ? departmentOptions.find(
                                        (dept) =>
                                          String(dept.value) === String(value)
                                      )?.label || "Select Department"
                                    : "Select Department"}
                                </span>
                                <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="p-0 relative"
                              side="bottom"
                            >
                              <Command className="w-full">
                                <CommandList>
                                  <CommandInput placeholder="Search Department..." />
                                  <CommandEmpty>No Department found.</CommandEmpty>
                                  <CommandGroup>
                                    {departmentOptions.map((dept) => (
                                      <CommandItem
                                        value={String(dept.label)}
                                        key={dept.value}
                                        className="capitalize"
                                        onSelect={() => {
                                          onChange(dept.value);
                                          setSelectedDepartment(
                                            String(dept.value)
                                          );
                                          setValue("service_id", ""); // Reset service when department changes
                                        }}
                                      >
                                        <Check
                                          className={cn(
                                            "mr-2 h-4 w-4 rounded-full border-2 border-green-500",
                                            String(dept.value) === String(value)
                                              ? "opacity-100"
                                              : "opacity-0"
                                          )}
                                        />
                                        {dept.label}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="h-[20px] text-red-500 text-xs mt-[5px]">
                          {errors.department_id?.message || ""}
                        </div>
                      </>
                    )}
                  />
                </div>

                {/* Service Dropdown */}
                <div className="relative">
                  <Controller
                    control={control}
                    name="service_id"
                    rules={{ required: "Required" }}
                    render={({ field: { onChange, value } }) => (
                      <>
                        <div className="relative w-full">
                          <label className="absolute text-xs font-medium left-2 -top-2 z-10 bg-white px-1 text-gray-600">
                            Service
                            <span className="text-red-500">*</span>
                          </label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                className="capitalize w-full scale-100 shadow-sm border-checkboxborder border-[1px] bg-white text-gray-600 hover:bg-white"
                                disabled={!selectedDepartment}
                              >
                                <span className="w-full text-left font-normal">
                                  {value && serviceOptions.length > 0
                                    ? serviceOptions.find(
                                        (service) =>
                                          String(service.value) ===
                                          String(value)
                                      )?.label || "Select Service"
                                    : selectedDepartment
                                      ? "Select Service"
                                      : "Select Service"}
                                </span>
                                <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="p-0 relative"
                              side="bottom"
                            >
                              <Command className="w-full">
                                <CommandList>
                                  <CommandInput placeholder="Search Service..." />
                                  <CommandEmpty>
                                    No Service found.
                                  </CommandEmpty>
                                  <CommandGroup>
                                    {serviceOptions.map((service) => (
                                      <CommandItem
                                        value={String(service.label)}
                                        key={service.value}
                                        className="capitalize"
                                        onSelect={() => {
                                          onChange(service.value);
                                        }}
                                      >
                                        <Check
                                          className={cn(
                                            "mr-2 h-4 w-4 rounded-full border-2 border-green-500",
                                            String(service.value) ===
                                              String(value)
                                              ? "opacity-100"
                                              : "opacity-0"
                                          )}
                                        />
                                        {service.label}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="h-[20px] text-red-500 text-xs mt-[5px]">
                          {errors.service_id?.message || ""}
                        </div>
                      </>
                    )}
                  />
                </div>
                {/* Counter */}
                <div className="relative">
                  <Controller
                    control={control}
                    name="counter_id"
                    rules={{ required: "Required" }}
                    render={({ field: { onChange, value } }) => (
                      <>
                        <div className="relative w-full">
                          <label className="absolute text-xs font-medium left-2 -top-2 z-10 bg-white px-1 text-gray-600">
                            Counter
                            <span className="text-red-500">*</span>
                          </label>
                          <Select
                            onValueChange={(value) => onChange(value)}
                            value={value ? String(value) : undefined}
                            disabled={!selectedService || isCounterDataLoading}
                          >
                            <SelectTrigger className="text-black w-full capitalize">
                              <SelectValue
                                placeholder={
                                  isCounterDataLoading
                                    ? "Loading counters..."
                                    : !selectedService
                                      ? "Select service first"
                                      : "Select Counter"
                                }
                              />
                            </SelectTrigger>
                            <SelectContent className="capitalize max-h-56 custom-scrollbar">
                              {isCounterDataLoading ? (
                                <div className="flex items-center justify-center p-2">
                                  <span className="text-sm text-gray-500">
                                    Loading...
                                  </span>
                                </div>
                              ) : counterOptions.length > 0 ? (
                                counterOptions.map((item) => (
                                  <SelectItem
                                    key={item.value}
                                    value={String(item.value)}
                                  >
                                    {item.label}
                                  </SelectItem>
                                ))
                              ) : (
                                <div className="flex items-center justify-center p-2">
                                  <span className="text-sm text-gray-500">
                                    No counters available for this service
                                  </span>
                                </div>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="h-[20px] text-red-500 text-xs mt-[5px]">
                          {errors.counter_id?.message || ""}
                        </div>
                      </>
                    )}
                  />
                </div>
                {/* Title with Tags Dropdown */}
                <div className="relative col-span-1 md:col-span-2">
                  <div className="flex flex-col gap-2">
                    <label className="absolute text-xs font-medium left-2 -top-2 z-10 bg-white px-1 text-gray-600">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Popover
                        open={isTitlePopoverOpen}
                        onOpenChange={setIsTitlePopoverOpen}
                      >
                        <PopoverTrigger asChild>
                          <div className="relative w-full">
                            <Controller
                              control={control}
                              name="title"
                              rules={{ required: "Required" }}
                              render={({ field }) => (
                                <input
                                  type="text"
                                  className={`w-full h-10 px-3 py-2 border rounded-md ${
                                    errors.title
                                      ? "border-red-500"
                                      : "border-gray-300"
                                  }`}
                                  value={titleInput}
                                  onChange={(e) => {
                                    handleTitleChange(e);
                                    field.onChange(e);
                                  }}
                                  placeholder="Enter or select a title"
                                  onClick={() => setIsTitlePopoverOpen(true)}
                                  ref={field.ref}
                                  onBlur={field.onBlur}
                                />
                              )}
                            />
                            <ChevronDownIcon className="absolute right-3 top-3 h-4 w-4 opacity-50" />
                          </div>
                        </PopoverTrigger>
                        <PopoverContent className="p-0 w-full" align="start">
                          {isServiceTagsLoading ? (
                            <div className="p-4 text-sm text-center">
                              Loading tags...
                            </div>
                          ) : serviceTags?.tags &&
                            serviceTags.tags.length > 0 ? (
                            <Command>
                              <CommandInput placeholder="Search tags..." />
                              <CommandList>
                                <CommandGroup>
                                  {serviceTags.tags.map((tag, index) => (
                                    <CommandItem
                                      key={index}
                                      onSelect={() => handleTagSelect(tag)}
                                      className="cursor-pointer"
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4",
                                          selectedTag === tag
                                            ? "opacity-100"
                                            : "opacity-0"
                                        )}
                                      />
                                      {tag}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          ) : (
                            <div className="p-4 text-sm text-center">
                              No tags available for this service
                            </div>
                          )}
                        </PopoverContent>
                      </Popover>
                      <div className="h-[20px] text-red-500 text-xs mt-[5px]">
                        {errors.title?.message || ""}
                      </div>
                    </div>
                  </div>
                </div>
                {/* ITS Number */}
                <div className="relative">
                  <div className="pb-2">
                    {/* <div className="flex flex-col gap-2 relative">
                      <label className="text-sm font-medium">Search by ITS</label> */}
                    <div className="relative ">
                      <span className="absolute p-0 text-xs left-12 -top-2 px-1 bg-white z-10">
                        Search by ITS <span className="text-red-500">*</span>
                      </span>
                      <div className="flex items-center relative w-auto">
                        <i className="fa-solid fa-magnifying-glass size-4 text-gray-400 absolute left-1 z-10 ml-2"></i>
                        <input
                          type="text"
                          className="p-2 pl-8 border rounded-md w-full"
                          value={itsSearchTerm}
                          onChange={(e) => setItsSearchTerm(e.target.value)}
                          placeholder="Search by ITS number"
                        />
                      </div>
                      {itsUserResults.length > 0 && (
                        <div className="absolute z-40 top-[70px] left-0 right-0 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {itsUserResults.map((user) => (
                            <div
                              key={user.username || user.id}
                              className="p-2 border-b last:border-none cursor-pointer hover:bg-gray-100 flex flex-col gap-1"
                              onClick={() => handleSelectItsUser(user)}
                            >
                              <div className="font-medium">{user.username}</div>
                              <div>
                                <span className="text-gray-500 text-sm font-normal capitalize">
                                  {user.first_name} {user.last_name}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500">
                                {user.email}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="relative col-span-1 md:col-span-2">
                  <FloatingLabelInput
                    id="description"
                    placeholder="Enter Description"
                    type="textarea"
                    rows={4}
                    maxLength={250}
                    {...form.register("description", {
                      maxLength: {
                        value: 250,
                        message: "Should be 250 characters or less",
                      },
                    })}
                    error={errors.description?.message}
                  />
                </div>

                {/* Customer Name */}
                <div className="relative">
                  <FloatingLabelInput
                    id="customer_name"
                    label="Customer Name"
                    className="capitalize w-full"
                    {...form.register("customer_name")}
                    disabled={true} // Auto-populated
                  />
                </div>

                {/* Phone Number */}
                <div className="relative">
                  <Controller
                    control={control}
                    name="phone_number"
                    render={({ field }) => (
                      <div className="relative ">
                        <span className="absolute p-0 text-xs left-12 -top-2 px-1 bg-white z-10">
                          Phone Number <span className="text-red-500">*</span>
                        </span>
                        <PhoneInputField
                          value={field.value || ""}
                          onChange={(value) => field.onChange(value)}
                          disabled={true}
                        />
                      </div>
                    )}
                  />
                </div>

                {/* Email */}
                <div className="relative">
                  <FloatingLabelInput
                    id="email"
                    label="Email"
                    className="w-full"
                    {...form.register("email")}
                    disabled={true} // Auto-populated
                  />
                </div>
                {/* Status */}
                <div className="relative">
                  <Controller
                    control={control}
                    name="status"
                    rules={{ required: "Required" }}
                    render={({ field: { onChange, value } }) => {
                      // Check if selected date is in the future
                      const selectedDate = watch("appointment_date");
                      const isCurrentDate =
                        selectedDate === new Date().toISOString().split("T")[0];
                      // Always use NEW for current date, RESERVED for future date
                      const statusValue = isCurrentDate ? "NEW" : "RESERVED";

                      // Auto-set the status value based on date
                      React.useEffect(() => {
                        if (selectedDate) {
                          onChange(statusValue);
                        }
                      }, [selectedDate, onChange, statusValue]);

                      return (
                        <>
                          <div className="relative w-full">
                            <label className="absolute text-xs font-medium left-2 -top-2 z-10 bg-white px-1 text-gray-600">
                              Status
                              <span className="text-red-500">*</span>
                            </label>
                            <div className="p-2 border rounded-md bg-gray-50 text-sm capitalize">
                              {statusValue}
                              <span className="text-xs italic ml-2">
                                {isCurrentDate
                                  ? "(Automatically set for current date)"
                                  : "(Automatically set for future dates)"}
                              </span>
                            </div>
                          </div>
                          <div className="h-[20px] text-red-500 text-xs mt-[5px]">
                            {errors.status?.message || ""}
                          </div>
                        </>
                      );
                    }}
                  />
                </div>

                {/* Assigned To */}
                <div className="relative">
                  <Controller
                    control={control}
                    name="assigned_to"
                    render={({ field: { onChange, value } }) => (
                      <>
                        <div className="relative w-full">
                          <label className="absolute text-xs font-medium left-2 -top-2 z-10 bg-white px-1 text-gray-600">
                            Assignee
                          </label>
                          <Popover modal={true}>
                            <PopoverTrigger asChild >
                              <Button className="capitalize w-full scale-100 shadow-sm border-checkboxborder border-[1px] bg-white text-gray-600 hover:bg-white">
                                <span className="w-full text-left font-normal">
                                  {value
                                    ? staffOptions?.find(
                                        (staff) =>
                                          String(staff.value) === String(value)
                                      )?.label || "Select Assignee"
                                    : "Select Assignee"}
                                </span>
                                <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="p-0 relative"
                              side="bottom"
                            >
                              <Command className="w-full">
                                <CommandList>
                                  <CommandInput placeholder="Search Staff..." />
                                  <CommandEmpty>
                                    No Khidmat Guzaar found.
                                  </CommandEmpty>
                                  <CommandGroup>
                                    {staffOptions?.map((staff) => (
                                      <CommandItem
                                        value={String(staff.label)}
                                        key={staff.value}
                                        className="capitalize"
                                        onSelect={() => onChange(staff.value)}
                                      >
                                        <Check
                                          className={cn(
                                            "mr-2 h-4 w-4 rounded-full border-2 border-green-500",
                                            String(staff.value) ===
                                              String(value)
                                              ? "opacity-100"
                                              : "opacity-0"
                                          )}
                                        />
                                        {staff.label}
                                        {staff.designation && (
                                          
                                          <span className="text-xs text-gray-500 ml-2">
                                            {staff.designation}
                                          </span>
                                        )}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                      </>
                    )}
                  />
                </div>

                {/* Appointment Date */}
                <div className="relative">
                  <FloatingLabelInput
                    id="appointment_date"
                    type="date"
                    label="Appointment Date"
                    text="*"
                    min={new Date().toISOString().split("T")[0]}
                    className="w-full"
                    {...form.register("appointment_date", {
                      required: "Required",
                    })}
                    error={errors.appointment_date?.message}
                  />
                </div>

                {/* Appointment Time */}
                <div className="relative">
                  <FloatingLabelInput
                    id="appointment_time"
                    type="time"
                    label="Appointment Time"
                    text="*"
                    className="w-full"
                    {...form.register("appointment_time", {
                      required: "Required",
                      validate: (value) => {
                        const selectedDate = watch("appointment_date");
                        // Only validate time if date is today
                        if (
                          selectedDate ===
                          new Date().toISOString().split("T")[0]
                        ) {
                          const now = new Date();
                          const currentTime = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
                          return (
                            value >= currentTime ||
                            "Time must be in equal to now or greater"
                          );
                        }
                        return true;
                      },
                    })}
                    error={errors.appointment_time?.message}
                  />
                </div>
              </div>
            </SheetDescription>

            <ConfirmationDialog
              isOpen={isDialogOpen}
              onClose={closeDialog}
              onConfirm={handleConfirm}
            />
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default AddTicketForm;
